import { useEffect } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { PushContentArgsInput, UpsertTemplateResult } from 'modules/graphql/types';
import { addNegativeToast } from 'modules/toastContainer';
import noOp from 'utils/noop';
import { isEmpty } from '@iterable/utils';
import { Config } from './types';

export type UpdatePushTemplateRequest = {
  cacheDataFeed: boolean;
  campaignId?: number;
  content: PushContentArgsInput;
  dataFeedIds: number[];
  mergeDataFeedContext: boolean;
  messageTypeId: number;
  name: string;
  referenceDataFeedsByAlias: boolean;
  templateId: number;
  //   destinationFolderId: number;
};

type UpdatePushTemplateResponse = {
  updatePushTemplate: UpsertTemplateResult;
};

export const UpdatePushTemplateMutation = gql`
  mutation UpsertPushTemplate(
    $cacheDataFeed: Boolean!
    $campaignId: Long
    $content: PushContentArgsInput!
    $dataFeedIds: [Long!]!
    $messageTypeId: Long!
    $mergeDataFeedContext: Boolean!
    $name: String!
    $referenceDataFeedsByAlias: Boolean!
    $templateId: Long!
  ) {
    updatePushTemplate(
      cacheDataFeed: $cacheDataFeed
      campaignId: $campaignId
      content: $content
      dataFeedIds: $dataFeedIds
      mergeDataFeedContext: $mergeDataFeedContext
      messageTypeId: $messageTypeId
      name: $name
      referenceDataFeedsByAlias: $referenceDataFeedsByAlias
      templateId: $templateId
    ) {
      failure {
        ... on InvalidTemplateContents {
          errors {
            ... on InvalidTemplateContent {
              reasons {
                ... on Unrenderable {
                  fieldName
                  msg
                }
                ... on MissingSnippets {
                  snippets
                }
                ... on InvalidSnippetParams {
                  snippetsWithParams
                }
              }
              content {
                ... on PushContentArgs {
                  locale {
                    name
                  }
                }
              }
            }
          }
        }
        ... on TemplateNotFound {
          templateId
        }
        __typename
      }
      templateId
    }
  }
`;

export function useUpdatePushTemplate({
  onComplete = noOp,
  onError = noOp,
  onSuccess = noOp,
}: Config) {
  const dispatch = useDispatch();
  const [upsertTemplate, data] = useMutation<UpdatePushTemplateResponse, UpdatePushTemplateRequest>(
    UpdatePushTemplateMutation,
  );
  const fieldErrors: Record<string, string> = {};
  useEffect(() => {
    if (!data.called) {
      return;
    }
    if (!data.loading && data.error) {
      dispatch(addNegativeToast('An unknown error occurred'));
    }

    if (!data.loading && data?.data?.updatePushTemplate) {
      const { templateId } = data.data.updatePushTemplate;
      if (templateId) {
        onSuccess(templateId);
      } else if (!isEmpty(fieldErrors)) {
        onError(fieldErrors);
      }
    }
    onComplete();
  }, [data.loading]);
  return [upsertTemplate, data] as const;
}
